// Platform Detection Utilities

export interface PlatformInfo {
  isWeb: boolean;
  isChromeOS: boolean;
  isAndroid: boolean;
  isIOS: boolean;
  isWindows: boolean;
  isMacOS: boolean;
  isLinux: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isPWA: boolean;
  supportsWebPush: boolean;
}

export function getPlatformInfo(): PlatformInfo {
  const userAgent = navigator.userAgent.toLowerCase();
  const platform = navigator.platform.toLowerCase();

  // Basic platform detection
  const isWeb = !window.electron && !(window as any).circadia;
  const isChromeOS = userAgent.includes("cros");
  const isAndroid = userAgent.includes("android");
  const isIOS =
    /ipad|iphone|ipod/.test(userAgent) ||
    (platform === "macintel" && navigator.maxTouchPoints > 1);
  const isWindows = userAgent.includes("windows") || platform.includes("win");
  const isMacOS = userAgent.includes("macintosh") || platform.includes("mac");
  const isLinux = userAgent.includes("linux") && !isAndroid && !isChromeOS;

  // Device type detection
  const isMobile = /mobi|android|iphone|ipod/i.test(userAgent);
  const isTablet =
    /ipad|android(?!.*mobi)|tablet/i.test(userAgent) ||
    (isChromeOS && "ontouchstart" in window);
  const isDesktop = !isMobile && !isTablet;

  // PWA detection
  const isPWA =
    window.matchMedia("(display-mode: standalone)").matches ||
    window.matchMedia("(display-mode: fullscreen)").matches ||
    (window.navigator as any).standalone === true;

  // Web Push support detection
  const supportsWebPush =
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;

  return {
    isWeb,
    isChromeOS,
    isAndroid,
    isIOS,
    isWindows,
    isMacOS,
    isLinux,
    isMobile,
    isTablet,
    isDesktop,
    isPWA,
    supportsWebPush,
  };
}

export function isChromeOS(): boolean {
  return getPlatformInfo().isChromeOS;
}

export function isWebPlatform(): boolean {
  return getPlatformInfo().isWeb;
}

export function isPWAMode(): boolean {
  return getPlatformInfo().isPWA;
}

export function supportsWebPushNotifications(): boolean {
  return getPlatformInfo().supportsWebPush;
}

// Enhanced tablet detection for ChromeOS
export function isTabletOrChromeOS(): boolean {
  const platformInfo = getPlatformInfo();
  return platformInfo.isTablet || platformInfo.isChromeOS;
}

// Get display mode
export function getDisplayMode(): string {
  if (window.matchMedia("(display-mode: standalone)").matches) {
    return "standalone";
  }
  if (window.matchMedia("(display-mode: fullscreen)").matches) {
    return "fullscreen";
  }
  if (window.matchMedia("(display-mode: minimal-ui)").matches) {
    return "minimal-ui";
  }
  return "browser";
}
